import React from 'react';
import { Clock } from 'lucide-react';
import { TONES, actionsFor, closedReason, isWalkIn } from '../../../lib/eventJourney';
import { GuestIcon, OwnerAvatar, TonePill, cardSub } from './guestUi';

const EMAIL_ACTIONS = ['invite', 'nudge', 'remind', 'thanks'];

const METHOD_FACTS = {
    face_match: ['Face match', 'scan-face'], face: ['Face match', 'scan-face'], manual: ['Checked in by hand', 'check'],
};

/**
 * The facts a card keeps from earlier in the journey, so it never loses where it came from: Walk-in,
 * Attended / No-show once the card has moved on, how they checked in, the close reason, Asked to reconfirm.
 */
export function cardFacts(card, columnKey, columns) {
    const index = (key) => columns.findIndex((column) => column.key === key);
    const facts = [];
    if (isWalkIn(card)) facts.push({ label: 'Walk-in', tone: 'violet', icon: 'footprints', tip: 'Created at the door' });
    else if (card.attendance_status === 'attended' && index(columnKey) > index('attended')) facts.push({ label: 'Attended', tone: 'teal', icon: 'badge-check' });
    else if (card.attendance_status === 'no_show' && columnKey !== 'no_show') facts.push({ label: 'No-show', tone: 'red', icon: 'user-x' });
    if (columnKey === 'going') facts.push({ label: 'RSVP yes', tone: 'emerald', icon: 'user-check', tip: 'Confirmed' });
    if (columnKey === 'attended' && !isWalkIn(card)) {
        const [label, icon] = METHOD_FACTS[card.attendance_method] || ['At the door', 'check'];
        facts.push({ label, tone: 'teal', icon, tip: 'How they were checked in' });
    }
    if (columnKey === 'closed') facts.push({ label: closedReason(card), tone: 'stone', icon: 'archive' });
    if (columnKey === 'converted') facts.push({ label: 'Handed to sales', tone: 'emerald', icon: 'briefcase' });
    // #919-06: a guest who registered through a form says which one.
    if (card.registration_form_id) facts.push({ label: `Form · ${card.registration_form_name || 'registration form'}`, tone: 'blue', icon: 'file-text', tip: 'Registered through this form' });
    if (columnKey === 'invited' && card.reconfirm_requested_at) facts.push({ label: 'Asked to reconfirm', tone: 'amber', icon: 'calendar', tip: 'The event moved; they were Going' });
    return facts;
}

/**
 * One guest on the board. The card is a group, not a button: the name opens the drawer (Enter), the box
 * selects (Space) and each action is its own button, so the keyboard reaches everything a mouse can. Drag it
 * to another column, or use "Move to…" in the drawer or bulk bar.
 */
export default function GuestCard({
    card, columnKey, columns, phase, due, selected, flash, dragging, readOnly,
    onOpen, onToggle, onAction, onDragStart, onDragEnd,
}) {
    const facts = cardFacts(card, columnKey, columns);
    const actions = actionsFor(card, columnKey, phase);
    const dueTone = due ? TONES[due.tone] || TONES.stone : null;
    const border = selected ? '#0C0A09' : flash ? '#10B981' : '#E9E7E2';
    return (
        <article
            data-testid={`guest-card-${card.contact_id}`}
            data-column={columnKey}
            aria-label={`${card.name}, ${columnKey.replace(/_/g, ' ')}`}
            draggable={!readOnly}
            onDragStart={(event) => {
                try { event.dataTransfer.setData('text/plain', card.contact_id); event.dataTransfer.effectAllowed = 'move'; } catch { /* a test DataTransfer */ }
                onDragStart?.(card);
            }}
            onDragEnd={() => onDragEnd?.()}
            onClick={(event) => { if (!event.target.closest('button, input, a')) onOpen(card); }}
            className={`flex flex-col gap-2 rounded-[9px] border bg-white px-[11px] py-2.5 shadow-sm hover:border-stone-400 ${readOnly ? '' : 'cursor-grab'} ${dragging ? 'opacity-50' : ''}`}
            style={{ borderColor: border, boxShadow: flash ? '0 0 0 3px #D1FAE5' : undefined }}
        >
            <div className="flex items-start gap-2">
                <button type="button" onClick={() => onOpen(card)} className="min-w-0 flex-1 truncate text-left text-[13px] font-semibold text-stone-900 hover:underline">{card.name}</button>
                <input type="checkbox" aria-label={`Select ${card.name}`} checked={selected} onChange={() => onToggle(card)} className="mt-0.5 h-4 w-4 shrink-0 accent-stone-900" />
            </div>
            <span className="truncate text-xs text-stone-500">{cardSub(card)}</span>
            <div className="flex flex-wrap items-center gap-1">
                <OwnerAvatar card={card} />
                {facts.map((fact) => <TonePill key={fact.label} tone={fact.tone} icon={fact.icon} title={fact.tip}>{fact.label}</TonePill>)}
            </div>
            {due && (
                <span data-testid={`guest-card-due-${card.contact_id}`} className="inline-flex h-5 w-max max-w-full items-center gap-1 rounded-[5px] border px-1.5 text-[11px] font-medium"
                    style={{ background: dueTone.bg, color: dueTone.fg, borderColor: dueTone.bd }}>
                    <Clock className="h-3 w-3 shrink-0" aria-hidden="true" /><span className="truncate">{due.text}</span>
                </span>
            )}
            {actions.length > 0 && (
                <div className="flex gap-1.5">
                    {actions.map((action) => {
                        const needsEmail = EMAIL_ACTIONS.includes(action.kind) && !card.email;
                        return (
                            <button key={action.label} type="button" data-testid={`guest-card-action-${action.label}`}
                                title={needsEmail ? 'This contact has no email address' : action.tip}
                                disabled={readOnly || needsEmail}
                                onClick={() => onAction(card, action)}
                                className="inline-flex h-[26px] flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-stone-200 bg-white px-1.5 text-xs font-medium text-stone-900 hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-50">
                                <GuestIcon name={action.icon} className="h-3 w-3" />{action.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </article>
    );
}
